import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { toneClass, type Tone } from './Tone';
import { useToneArea } from './ToneArea';
import styles from './Chip.module.css';

// 06-qol §11: status colours are consistent system-wide and never repurposed. A chip
// draws in the app's tones (ADR 0035): a status shown by its own tone from
// `status-tones.ts`, or one of the older names below. None is grey — a neutral label
// (a tag, a skill) takes the colour of the area it is in (DECISIONS-LOG 2026-10-06).
export type ChipTone = 'success' | 'warning' | 'danger' | 'progress' | 'neutral' | 'attention';

/** Solid for state that drives work; tonal for secondary or contextual labels. */
export type ChipEmphasis = 'solid' | 'tonal';

const NAMED_TONES: Readonly<Record<Exclude<ChipTone, 'neutral'>, Tone>> = {
  success: 'done',
  warning: 'attention',
  danger: 'urgent',
  progress: 'progress',
  attention: 'attention',
};

function isChipTone(tone: ChipTone | Tone): tone is ChipTone {
  return tone === 'neutral' || tone in NAMED_TONES;
}

/** The app tone a chip or dot draws in; neutral follows the area. */
function useChipTone(tone: ChipTone | Tone): Tone {
  const area = useToneArea();
  if (tone === 'neutral') return area.tone;
  return isChipTone(tone) ? NAMED_TONES[tone] : tone;
}

interface ChipProps {
  readonly tone?: ChipTone | Tone;
  readonly emphasis?: ChipEmphasis;
  /** Status words are upper-case inside chips; a name or a tag is not shouted at. */
  readonly plain?: boolean;
  readonly onRemove?: () => void;
  readonly removeLabel?: string;
  readonly children: ReactNode;
}

export function Chip({
  tone = 'neutral',
  emphasis = 'tonal',
  plain = false,
  onRemove,
  removeLabel,
  children,
}: ChipProps): ReactNode {
  const appTone = useChipTone(tone);
  const classes = [
    toneClass,
    styles.chip,
    emphasis === 'solid' ? styles.solid : styles.tonal,
    plain ? styles.plain : '',
    onRemove ? styles.removable : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} data-tone={appTone}>
      {children}
      {onRemove && (
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label={removeLabel ?? 'Remove'}
        >
          <X size={12} aria-hidden />
        </button>
      )}
    </span>
  );
}

interface DotProps {
  readonly tone: ChipTone | Tone;
  readonly children: ReactNode;
}

/**
 * Priority and secondary dimensions are dots beside neutral ink, never chips, so
 * they never compete with the status chip in the same row.
 */
export function Dot({ tone, children }: DotProps): ReactNode {
  const appTone = useChipTone(tone);
  return (
    <span className={styles.dot}>
      <span className={`${toneClass} ${styles.dotMark}`} data-tone={appTone} aria-hidden />
      {children}
    </span>
  );
}
